import { ComponentRef, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { RepositoryService } from "../linq-utils/repository.service";
import { Link, WelcomeDialogComponent, WelcomeDialogContent } from "./popups/welcome-dialog";
import { ModelDetails, Organisation } from "../common/organisation";
import { NodeService } from "../model-services/node.service";
import { NODE_TYPE } from "../common/linqset-model";
import { IntroductionDialogComponent } from "./popups/introduction-dialog";
import { HomeIntroDialogComponent } from "./popups/home-intro-dialog";
import { UserService } from "../user/user.service";
import { ONBOARDING_STEPS } from "../common/user";
import { ConnectedPosition, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { HelpOverlayComponent } from "./whats-this/help-overlay.component";
import { HelpChatComponent } from "./help-chat/help-chat.component";

const COMPONENT = "HelpService"

export class HelpOverlay {
    constructor(public element:string, public message:string, public position: ConnectedPosition) {}
}

// the other places a tour message can go if the preferred one doesn't fit on screen
const FALLBACK_POSITIONS:ConnectedPosition[] = [
    {originX:'center', originY:'bottom', overlayX:'center', overlayY:'top'},
    {originX:'center', originY:'top', overlayX:'center', overlayY:'bottom'},
    {originX:'end', originY:'center', overlayX:'start', overlayY:'center'},
    {originX:'start', originY:'center', overlayX:'end', overlayY:'center'},
    {originX:'center', originY:'center', overlayX:'center', overlayY:'center'},
]

interface TourStep {
    element:HTMLElement
    message:string
    position:ConnectedPosition
}

@Injectable()
export class HelpService {
    constructor(private dialog:MatDialog, private repoService:RepositoryService, private nodeService:NodeService,
        private userService:UserService, private overlay: Overlay) {

    }

    private tour?:{steps:TourStep[], index:number, overlayRef?:OverlayRef, highlighted?:HTMLElement}
    private tourKeys = (event:KeyboardEvent) => {
        if (!this.tour) return
        if (event.key === 'Escape') this.endTour()
        else if (event.key === 'ArrowRight') this.showTourStep(this.tour.index + 1)
        else if (event.key === 'ArrowLeft') this.showTourStep(this.tour.index - 1)
    }

    // explains each part of the screen in turn. The user moves through it with Next and Back (or the arrow keys) and can end it at any time.
    // Parts that aren't on screen, such as a closed panel, are skipped
    showWhatsThisOverlay(elements:HelpOverlay[]) {
        this.endTour()
        const steps:TourStep[] = elements.map(e => ({
            element: (document.getElementById(e.element) || document.getElementsByClassName(e.element)?.item(0)) as HTMLElement,
            message: e.message,
            position: e.position,
        })).filter(s => s.element && s.element.getClientRects().length > 0 && s.element.offsetWidth > 0 && s.element.offsetHeight > 0)
        if (steps.length === 0) return

        this.tour = {steps, index:0}
        document.addEventListener('keydown', this.tourKeys)
        this.showTourStep(0)
    }

    private showTourStep(index:number) {
        const tour = this.tour
        if (!tour) return
        if (index >= tour.steps.length) return this.endTour()
        if (index < 0) return

        this.clearTourStep()
        tour.index = index
        const step = tour.steps[index]
        // the page may have changed since the tour started
        if (!document.body.contains(step.element)) return this.showTourStep(index + 1)

        step.element.scrollIntoView({block:'nearest', inline:'nearest'})
        step.element.classList.add('help-highlight')
        tour.highlighted = step.element

        tour.overlayRef = this.overlay.create({
            hasBackdrop: false,
            panelClass: ["overlay-class", "tour-overlay"],
            width: 340,
            maxWidth: '90vw',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            positionStrategy: this.overlay.position().flexibleConnectedTo(step.element)
                .withPositions([step.position, ...FALLBACK_POSITIONS])
                .withPush(true)
                .withViewportMargin(8)
        });
        const ref = tour.overlayRef.attach(new ComponentPortal(HelpOverlayComponent))
        ref.setInput('message', step.message)
        ref.setInput('step', index + 1)
        ref.setInput('count', tour.steps.length)
        ref.instance.next.subscribe(() => this.showTourStep(index + 1))
        ref.instance.back.subscribe(() => this.showTourStep(index - 1))
        ref.instance.end.subscribe(() => this.endTour())
    }

    private clearTourStep() {
        this.tour?.highlighted?.classList.remove('help-highlight')
        this.tour?.overlayRef?.dispose()
        if (this.tour) {
            this.tour.highlighted = undefined
            this.tour.overlayRef = undefined
        }
    }

    endTour() {
        if (!this.tour) return
        this.clearTourStep()
        this.tour = undefined
        document.removeEventListener('keydown', this.tourKeys)
    }

    // the AI help chat, beside whatever the user is looking at
    openHelpChat() {
        return HelpChatComponent.open(this.dialog)
    }

    openIntroductonDialog() {
        this.userService.saveOnboardingStep(ONBOARDING_STEPS.HELP_VIDEOS)
        return this.dialog.open(IntroductionDialogComponent, {data:{initialStep:1}, width: "1000px", maxWidth: "95vw", maxHeight: "90vh", hasBackdrop:true});
    }

    openCreateOrganisationDialog() {
        const paragraphs = [
            "You are currently viewing the organisations that you have access to. Organisations provide the container for an enterprise, its <b>models</b> and <b>projects</b>.",
            "We have provided a demo organisation that you can open and browse the models provided.",
            "Help on getting started is available to the right of screen or via the main menu.",
            "Help messages can be turned on/off by clicking on the ? icon at the bottom right of the screen",
            "You can create your organisation or ask someone to add you to their organisation. <b>Click on the link below to create a new organisation.</b>"
        ]
        const data = new WelcomeDialogContent("Welcome to LINQ", paragraphs)
        data.links.push(new Link("create", "Create organisation"))
        return this.dialog.open(WelcomeDialogComponent, {data, width: "1000px", maxWidth: "95vw", hasBackdrop:true});
    }

    openCreateModelDialog(organisation:Organisation) {
        return HomeIntroDialogComponent.open(this.dialog, organisation)
    }

    openAIModelIntroductionDialog(mobile:boolean) {
        const paragraphs = [
            "<b>Congratulations you have generated your first model.</b>",
            "AI used the information you gave to generate a typical workflow to give you an idea of how LINQ works.",
            "You will see insights for the model in the panel on the right."
        ]
        if (!mobile) paragraphs.push("You can make changes to this model such that it better represents your own process or delete it and create your own model from scratch.")
        else {
            paragraphs.push("<b>You cannot edit or view detailed information on a mobile device. Switch to a laptop/desktop or larger screen to edit this model.</b>")
        }
        const data = new WelcomeDialogContent("AI generated model", paragraphs)
        return this.dialog.open(WelcomeDialogComponent, {data, width: "600px", hasBackdrop:true});
    }
}